import { marketingColors as C, marketingFonts as F, visualTokens as V, marketingDarkStyles } from '../../../theme_landing_page_2';
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MillosMark } from '../icons';
import Eyebrow from '../Eyebrow';

/**
 * Closing CTA section previewing mobile check-ins and planned messaging.
 * The unfolded-width device opens once on entry; notification cards cycle independently.
 */

const NOTIFICATIONS = [
  { dot: V.brandViolet, title: 'Portfolio check-in', body: 'Your compliance status, wherever you are.', delay: '0s' },
  { dot: V.brandCyan, title: 'WhatsApp update', body: 'An update from your AI agents.', delay: '2s' },
  { dot: V.brandTeal, title: 'Call from MillOS', body: 'Talk to your AI agents on the go.', delay: '4s' },
];
const compactCopy = '@media (max-width:1100px)';
const foldable = '@media (min-width:640px) and (max-width:1100px)';

const MillosAvailabilityCta: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setOpened(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setOpened(true);
      observer.disconnect();
    }, { threshold: 0.25 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return (
  <Box ref={sectionRef} component="section" aria-label="Coming soon on mobile" sx={{ ...marketingDarkStyles, padding: '12px', fontFamily: F.body, scrollMarginTop: '80px' }}>
    <style>{`
      @keyframes mlNf{0%,4%{opacity:0;transform:translateY(-14px) scale(.97)}9%,86%{opacity:1;transform:none}93%,100%{opacity:0;transform:translateY(-6px)}}
      @keyframes mlUnfold{
        0%,12%{clip-path:inset(0 50% 0 0 round 28px);transform:translateX(25%)}
        85%,100%{clip-path:inset(0 0 0 0 round 28px);transform:translateX(0)}
      }
      @keyframes mlUnfoldPanel{
        0%,12%{transform:perspective(900px) rotateY(-180deg);opacity:1}
        85%{transform:perspective(900px) rotateY(0deg);opacity:1}
        100%{transform:perspective(900px) rotateY(0deg);opacity:0}
      }
      @media (prefers-reduced-motion:reduce){.ml-nf{animation:none!important;opacity:1!important}}
    `}</style>
    <Box sx={{ maxWidth: 1392, margin: '0 auto', position: 'relative', overflow: 'hidden', borderRadius: V.borderRadius32px, background: C.heroGradient, padding: { xs: '32px 24px', md: '32px 40px' }, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      '@media (max-width:639px)': { padding: '20px 20px' },
    }}>
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: '32px', md: '48px' }, alignItems: 'center', justifyContent: 'center',
        '@media (max-width:639px)': { gap: '16px' },
        [foldable]: { flexDirection: 'row', gap: '32px' },
      }}>
        <Box sx={{ flex: { md: '1 1 420px' }, minWidth: 0, color: V.onDark,
          '@media (max-width:639px)': { textAlign: 'center' },
          [foldable]: { flex: '1 1 0' },
        }}>
          <Eyebrow variant="onImage">What&apos;s next</Eyebrow>

          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: V.fontWeight400, fontSize: 'clamp(36px, 5.5svh, 56px)', lineHeight: V.lineHeight1_08, letterSpacing: V.letterSpacing_0_03em, color: V.onDark, m: '24px 0 0',
            [compactCopy]: { fontSize: 28, mt: '12px' },
          }}>
            Your portfolio in your pocket.<Box component="span" sx={{ [compactCopy]: { display: 'none' } }}><br />Check in anytime.</Box>
          </Box>
          <Box component="p" sx={{ maxWidth: '29rem', mt: '18px', fontSize: V.fontSize17, lineHeight: V.lineHeight1_55, color: V.paperAlpha90, [compactCopy]: { display: 'none' } }}>
            See what your AI agents are doing and check your property compliance, wherever you are. Everything you need to stay informed, right on your phone.
          </Box>
          <Box sx={{ maxWidth: '29rem', fontSize: V.fontSize15_5, lineHeight: V.lineHeight1_55, color: V.paperAlpha90, [compactCopy]: { display: 'none' } }}>
            iOS &amp; Android apps — coming soon. WhatsApp updates and calls from your AI agents are planned too, so you can stay connected without opening the app.
          </Box>
          <Box component="p" sx={{ display: 'none', [compactCopy]: { display: 'block', fontSize: 13, lineHeight: 1.4, my: '12px', color: V.paperAlpha90 } }}>
            Check in anywhere.<br />iOS &amp; Android coming soon.<br />WhatsApp updates &amp; AI calls planned.
          </Box>

          <Box component={RouterLink} to="/register" sx={{ display: 'inline-flex', alignItems: 'center', gap: '9px', background: C.millosMint, color: V.buttonText, borderRadius: V.borderRadius32px, padding: '13px 24px', fontSize: V.fontSize16, fontWeight: V.fontWeight600, mt: '28px', '&:hover': { background: V.brandBlue, color: V.buttonText },
            [compactCopy]: { display: 'none' },
          }}>
            Try for free today <Box component="span">→</Box>
          </Box>
        </Box>

        {/* Responsive phone mock */}
        <Box aria-label="Preview of planned mobile updates" sx={{ flex: { md: '0 1 300px' }, display: 'flex', justifyContent: 'center', alignItems: 'center', maxWidth: '100%',
          '@media (max-width:639px)': { width: 165, height: 346.5, position: 'relative', display: 'block' },
          '@media (max-width:639px) and (max-height:700px)': { width: 114, height: 239.4 },
          [foldable]: { flex: '0 0 253px', width: 253, height: 313.5, position: 'relative', display: 'block' },
        }}>
          <Box sx={{ position: 'relative', width: 300, maxWidth: '100%',
            '@media (max-width:639px)': { maxWidth: 'none', transform: 'scale(0.55)', transformOrigin: 'top left' },
            '@media (max-width:639px) and (max-height:700px)': { transform: 'scale(0.38)' },
            [foldable]: { width: 460, maxWidth: 'none', transform: 'scale(0.55)', transformOrigin: 'top left' },
          }}>
            <Box data-mobile-device-frame sx={{ position: 'relative', borderRadius: V.borderRadius56px, padding: '11px', background: `linear-gradient(150deg,${V.metalLight},${V.metalMid} 46%,${V.metalHighlight} 78%,${V.metalDark})`, boxShadow: V.phoneShadow,
              [foldable]: {
                borderRadius: '28px',
                clipPath: opened ? undefined : 'inset(0 50% 0 0 round 28px)',
                transform: opened ? undefined : 'translateX(25%)',
                animation: opened ? 'mlUnfold 2.6s cubic-bezier(.25,.65,.3,1) both' : 'none',
                '&::after': {
                  content: '""', position: 'absolute', left: '50%', right: 11, top: 11, bottom: 11,
                  borderRadius: '0 16px 16px 0', transformOrigin: 'left center', pointerEvents: 'none', zIndex: 7,
                  background: `linear-gradient(165deg,${V.brandBlue},${V.midnight})`,
                  opacity: 0, animation: opened ? 'mlUnfoldPanel 2.6s cubic-bezier(.25,.65,.3,1) both' : 'none',
                },
                '@media (prefers-reduced-motion:reduce)': {
                  animation: 'none', clipPath: 'none', transform: 'none', '&::after': { display: 'none' },
                },
              },
            }}>
              <Box sx={{ borderRadius: V.borderRadius46px, background: V.black, padding: '4px', [foldable]: { borderRadius: '20px' } }}>
                <Box sx={{ position: 'relative', borderRadius: V.borderRadius42px, background: `linear-gradient(165deg,${V.glassDarkAlpha90} 0%,${V.midnight} 100%)`, minHeight: 'clamp(450px, calc(100svh - 300px), 520px)', overflow: 'hidden',
                  '@media (max-width:639px)': { minHeight: 600 },
                  [foldable]: {
                    minHeight: 540, borderRadius: '16px',
                  },
                }}>
                  <Box sx={{ position: 'absolute', top: 11, left: '50%', transform: 'translateX(-50%)', width: 104, height: 31, borderRadius: V.borderRadius20px, background: V.black, zIndex: 4,
                    [foldable]: { left: 'auto', right: 14, transform: 'none', width: 10, height: 10, borderRadius: '50%' },
                  }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 26px 0', fontSize: V.fontSize13, fontWeight: V.fontWeight600, color: V.onDark, position: 'relative', zIndex: 5 }}>
                    <Box component="span">MillOS</Box>
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', color: V.onDark, pt: '18px' }}>
                    <MillosMark size={16} style={{ margin: '0 auto' }} />
                    <Box sx={{ fontSize: V.fontSize15, fontWeight: V.fontWeight500, mt: '10px', opacity: 0.9 }}>Friday 16 October</Box>
                    <Box sx={{ fontFamily: F.statLabel, fontWeight: V.fontWeight600, fontSize: V.fontSize74, lineHeight: V.lineHeight0_95, letterSpacing: V.letterSpacing_0_04em, mt: '2px' }}>9:41</Box>
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', gap: '9px', padding: '22px 12px 0' }}>
                    {NOTIFICATIONS.map((n) => (
                      <Box
                        key={n.title}
                        className="ml-nf"
                        data-marketing-surface="light"
                        sx={{ opacity: 0, animation: 'mlNf 11s cubic-bezier(.2,.85,.3,1) infinite', animationDelay: n.delay, display: 'flex', gap: '8px', alignItems: 'flex-start', background: V.paperAlpha86, backdropFilter: V.backdropFilterblur_18px, border: `1px solid ${V.paperAlpha60}`, borderRadius: V.borderRadius20px, padding: '8px 10px', boxShadow: V.notificationShadow }}
                      >
                        <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: V.borderRadius9px, background: V.paper, display: 'grid', placeItems: 'center', flex: 'none', boxShadow: V.notificationIconShadow }}>
                          <MillosMark size={22} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <Box component="span" sx={{ fontFamily: F.statLabel, fontSize: V.fontSize11, fontWeight: V.fontWeight700, letterSpacing: V.letterSpacing0_06em, textTransform: 'uppercase', color: C.navy }}>MillOS</Box>
                            <Box component="span" sx={{ fontSize: V.fontSize11, color: V.bodySoft }}>now</Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '3px' }}>
                            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', background: n.dot, flex: 'none' }} />
                            <Box component="span" sx={{ fontFamily: F.statLabel, fontSize: V.fontSize14, fontWeight: V.fontWeight700, letterSpacing: V.letterSpacing_0_01em, color: C.navy }}>{n.title}</Box>
                          </Box>
                          <Box sx={{ fontSize: V.fontSize12, lineHeight: V.lineHeight1_35, color: V.body, mt: '2px', '@media (max-width:639px)': { display: 'none' } }}>{n.body}</Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 16, zIndex: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: '14px' }}>
                      <Box sx={{ width: 124, height: 5, borderRadius: V.borderRadius6px, background: V.paper, opacity: 0.85 }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

    </Box>
  </Box>
  );
};

export default MillosAvailabilityCta;
